import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Toggle,
  Button,
  Input,
  FormField,
  Alert,
  Box,
  ColumnLayout,
} from '@cloudscape-design/components';
import { ModelConfig } from '../types';
import { apiService } from '../services/ApiService';

export const AdminPanel: React.FC = () => {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // API Key management state
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({
    xai: '',
    openai: '',
    google: '',
  });
  const [testingConnection, setTestingConnection] = useState<string | null>(null);

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getModels();
      setModels(result.models);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModel = async (modelId: string, enabled: boolean) => {
    try {
      setError(null);
      setSuccess(null);
      await apiService.updateModelStatus(modelId, enabled);
      setModels(prev =>
        prev.map(m => (m.modelId === modelId ? { ...m, enabled } : m))
      );
      setSuccess(`Model ${modelId} ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update model status');
    }
  };

  const handleUpdateApiKey = async (provider: string) => {
    const apiKey = apiKeys[provider];
    if (!apiKey) {
      setError('Please enter an API key');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await apiService.updateApiKey(provider, apiKey);
      setSuccess(`API key for ${provider} updated successfully`);
      setApiKeys(prev => ({ ...prev, [provider]: '' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update API key');
    }
  };

  const handleTestConnection = async (provider: string) => {
    try {
      setError(null);
      setSuccess(null);
      setTestingConnection(provider);
      const result = await apiService.testConnection(provider);
      if (result.success) {
        setSuccess(`Connection test for ${provider} successful: ${result.message}`);
      } else {
        setError(`Connection test for ${provider} failed: ${result.message}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test connection');
    } finally {
      setTestingConnection(null);
    }
  };

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header variant="h2" description="Manage AI model configurations">
            Model Management
          </Header>
        }
      >
        {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
        {success && <Alert type="success" dismissible onDismiss={() => setSuccess(null)}>{success}</Alert>}

        <Table
          columnDefinitions={[
            {
              id: 'modelId',
              header: 'Model ID',
              cell: item => item.modelId,
            },
            {
              id: 'displayName',
              header: 'Display Name',
              cell: item => item.displayName,
            },
            {
              id: 'provider',
              header: 'Provider',
              cell: item => item.provider,
            },
            {
              id: 'enabled',
              header: 'Status',
              cell: item => (
                <Toggle
                  checked={item.enabled}
                  onChange={({ detail }) =>
                    handleToggleModel(item.modelId, detail.checked)
                  }
                >
                  {item.enabled ? 'Enabled' : 'Disabled'}
                </Toggle>
              ),
            },
          ]}
          items={models}
          loading={loading}
          loadingText="Loading models..."
          empty={
            <Box textAlign="center" color="inherit">
              <b>No models configured</b>
            </Box>
          }
        />
      </Container>

      <Container
        header={
          <Header variant="h2" description="Manage API keys for external providers">
            API Key Management
          </Header>
        }
      >
        <SpaceBetween size="l">
          {['xai', 'openai', 'google'].map(provider => (
            <ColumnLayout columns={3} key={provider}>
              <FormField label={`${provider.toUpperCase()} API Key`}>
                <Input
                  value={apiKeys[provider]}
                  onChange={({ detail }) =>
                    setApiKeys(prev => ({ ...prev, [provider]: detail.value }))
                  }
                  type="password"
                  placeholder="Enter new API key"
                />
              </FormField>
              <Box padding={{ top: 'l' }}>
                <Button
                  onClick={() => handleUpdateApiKey(provider)}
                  disabled={!apiKeys[provider]}
                >
                  Update Key
                </Button>
              </Box>
              <Box padding={{ top: 'l' }}>
                <Button
                  onClick={() => handleTestConnection(provider)}
                  loading={testingConnection === provider}
                  disabled={testingConnection !== null}
                >
                  Test Connection
                </Button>
              </Box>
            </ColumnLayout>
          ))}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
